/**
 * Naming the sensors behind a run's plotted series — used by the Aux
 * Sensor tab and by High Rate.
 *
 * The pipeline's channels are positional: it writes the selected sensor
 * of each type to the canonical channel (`RiderPwr_P`, `CrankCad_R`,
 * `WhlRspd_n`) and the second one to its `Alt` twin. That says which
 * series is which, and nothing at all about WHICH DEVICE each one is —
 * so the plots used to read "Power Meter 1" / "Power Meter 2". This
 * module resolves those positions back to real sensors, so a legend can
 * say "Primary - Front (ID 46145)" and "Aux - Rear (ID 6721)" instead.
 *
 * Where the answer comes from, in order:
 *   - `sensors_actual` — the device's observation, `hash_id -> run_instance`.
 *     This is the binding that ties a solve selection (recorded as an
 *     instance) back to a device.
 *   - `sensors_intent` — the user's claim: wheel position, and which
 *     sensor was slot 0 (`primary`).
 *   - `solve.selections` — which instance the pipeline actually plotted as
 *     primary. Wheel speed additionally records the id outright
 *     (`speed.ant_device_number`), so it needs no instance binding.
 *
 * Nothing here guesses. A series this cannot name keeps the positional
 * label it has today ("Power Meter 2"), which is what a run processed
 * before the intent/actual split does until it is reprocessed.
 */
import { SolveData, SensorsActual, SensorsIntent } from 'types/Run';
import { GroundSpeedSetup, SPEED_POSITION } from 'types/CalibrationParameters';
import {
  effectivePrimaryId,
  effectiveSetup,
  LegacySpeedFields,
  RunWheelLabelSource,
  sensorIdentity,
  speedSource,
  SENSOR_FUNCTION,
  antDeviceNumber,
  wheelSensorPair,
} from '../../CalibrationView/groundSpeedUtils';

// The role a series plays, which is what a label leads with: the plot's
// title already says WHAT is being measured, so the name only has to say
// which of the two sensors this is, and which device that is.
const PRIMARY_ROLE = 'Primary';
const AUX_ROLE = 'Aux';

// Which sensor function a cadence page family belongs to: cadence either
// rides on a power meter's own pages or comes from a speed+cadence sensor.
const CADENCE_FUNCTIONS: Record<string, string> = {
  bikepwr: SENSOR_FUNCTION.POWER,
  spdcad: SENSOR_FUNCTION.SPEED,
};

/** One named sensor: a legend label plus the parts it is built from. */
export interface AuxSensorName {
  /** ANT device number — for matching a name back to a sensor record. */
  id: number;
  /** e.g. 'Front (ID 46145)' — for places too tight for the full label. */
  identity: string;
  /** e.g. 'Primary - Front (ID 46145)' — a standalone legend entry. */
  label: string;
}

/** The two series positions every aux plot has. */
export interface AuxSensorLabels {
  primary?: AuxSensorName;
  alt?: AuxSensorName;
}

/**
 * The solve plotted a different sensor as primary than the calibration
 * names. Both roles carry a `*` and this is the footnote that explains it.
 */
export interface WheelPrimaryConflict {
  /** Identity of the sensor the calibration names, e.g. 'Front (ID 46145)'. */
  calibrated: string;
  /** Identity of the sensor the solve actually plotted as primary. */
  used: string;
  /** Ready-to-render footnote, leading with the `*`. */
  note: string;
}

export interface WheelSpeedLabels extends AuxSensorLabels {
  conflict?: WheelPrimaryConflict;
}

/** The run fields this module reads. */
export interface AuxSensorRun extends RunWheelLabelSource {
  sensors_intent?: SensorsIntent | null;
  sensors_actual?: SensorsActual | null;
  solve?: SolveData | null;
}

/** One sensor of a given function, as the two records jointly describe it. */
interface RosterEntry {
  /** ANT device number (hashId with the device type divided out). */
  id: number;
  /** PositionEnum ordinal from the claim; only speed sensors carry one. */
  position?: number;
  /** The `i{n}` instance its log pages carried — null when unobserved. */
  instance: number | null;
  /** The claim put this sensor in slot 0 of its function. */
  claimedPrimary: boolean;
}

/**
 * Every sensor of one function the run knows about. The claim and the
 * observation are unioned rather than intersected: a legacy run carries a
 * backfilled claim and no observation at all, and a sensor the user never
 * declared can still have been logged.
 */
function roster(run: AuxSensorRun | null | undefined, func: string): RosterEntry[] {
  const observed = (run?.sensors_actual?.sensors ?? []).filter(
    (s) => (s.source ?? 'sensor') === 'sensor' && s.function === func && s.hash_id != null,
  );
  const claimed = (run?.sensors_intent?.sensors ?? []).filter((s) => s.function === func);
  const hashes = [...new Set([
    ...observed.map((s) => s.hash_id as number), ...claimed.map((s) => s.hash_id),
  ])].sort((a, b) => a - b);
  return hashes.map((hash) => {
    const claim = claimed.find((s) => s.hash_id === hash);
    return {
      id: antDeviceNumber(hash),
      position: claim?.position,
      instance: observed.find((s) => s.hash_id === hash)?.run_instance ?? null,
      claimedPrimary: Boolean(claim?.primary),
    };
  });
}

/** What the solve recorded about which sensor it plotted as primary. */
interface PrimaryClues {
  /** An ANT device number — exact, and needs no instance binding. */
  id?: number | null;
  /** An `i{n}` instance — exact once `sensors_actual` binds it to a device. */
  instance?: number | null;
  /**
   * The ordinal selector (`alt_power` / `alt_spd` / `alt_cad`): "plot the
   * Nth discovered source". A last resort — see `byOrdinal`.
   */
  altSelector?: number | null;
}

/**
 * The sensor a bare ordinal selector points at. Sources are discovered in
 * ascending instance order and the claim's slots are reconciled against
 * that same order, so selector 0 is the sensor the claim marks `primary`.
 * Past that the claim records no ordering — only which sensor was slot 0 —
 * so selector 1 resolves only when there is exactly one other sensor it
 * could possibly mean, and nothing further resolves at all.
 */
function byOrdinal(
  entries: RosterEntry[], altSelector: number,
): RosterEntry | undefined {
  const claimed = entries.find((e) => e.claimedPrimary);
  if (!claimed) return undefined;
  if (altSelector === 0) return claimed;
  if (altSelector === 1 && entries.length === 2) {
    return entries.find((e) => e.id !== claimed.id);
  }
  return undefined;
}

/**
 * Split a roster into the sensor the pipeline plotted as primary and the
 * one it plotted as `Alt`. Clues are tried strongest first; a roster of one
 * resolves on its own. Anything left unresolved stays that way rather than
 * being guessed, and the second series is named only when exactly one
 * other sensor remains.
 */
function resolvePair(
  entries: RosterEntry[], clues: PrimaryClues,
): { primary?: RosterEntry; alt?: RosterEntry } {
  if (entries.length === 0) return {};
  let primary = clues.id == null ? undefined : entries.find((e) => e.id === clues.id);
  if (!primary && clues.instance != null) {
    primary = entries.find((e) => e.instance != null && e.instance === clues.instance);
  }
  if (!primary && entries.length === 1) [primary] = entries;
  if (!primary && clues.altSelector != null) primary = byOrdinal(entries, clues.altSelector);
  if (!primary) return {};
  const primaryId = primary.id;
  const others = entries.filter((e) => e.id !== primaryId);
  return { primary, alt: others.length === 1 ? others[0] : undefined };
}

function named(
  entry: RosterEntry | undefined, role: string, flagged = false,
): AuxSensorName | undefined {
  if (!entry) return undefined;
  const identity = sensorIdentity(entry.id, entry.position);
  return { id: entry.id, identity, label: `${role}${flagged ? '*' : ''} - ${identity}` };
}

/**
 * The two power meters behind `RiderPwr_P` and `RiderAltPwr_P`. Power
 * meters have no wheel position, so they read as a bare device number.
 */
export function powerMeterLabels(run: AuxSensorRun | null | undefined): AuxSensorLabels {
  const { primary, alt } = resolvePair(roster(run, SENSOR_FUNCTION.POWER), {
    instance: run?.solve?.selections?.power_meter?.instance,
    altSelector: run?.solve?.inputs?.alt_power,
  });
  return { primary: named(primary, PRIMARY_ROLE), alt: named(alt, AUX_ROLE) };
}

/**
 * The sources behind `CrankCad_R` and `CrankAltCad_R`. Cadence is the one
 * type whose two series can come from different kinds of device (a power
 * meter and a speed+cadence sensor), and only the primary's page family is
 * recorded — so the secondary is resolved by searching both functions for
 * its instance and is left unnamed when that is ambiguous.
 */
export function cadenceLabels(run: AuxSensorRun | null | undefined): AuxSensorLabels {
  const selection = run?.solve?.selections?.cadence;
  const func = CADENCE_FUNCTIONS[selection?.page_family ?? ''];
  if (!func) return {};
  const { primary } = resolvePair(roster(run, func), {
    instance: selection?.instance,
    altSelector: run?.solve?.inputs?.alt_cad,
  });
  if (!primary) return {};
  return {
    primary: named(primary, PRIMARY_ROLE),
    alt: named(cadenceSecondary(run, primary, selection?.secondary_instance), AUX_ROLE),
  };
}

function cadenceSecondary(
  run: AuxSensorRun | null | undefined,
  primary: RosterEntry,
  instance: number | null | undefined,
): RosterEntry | undefined {
  if (instance == null) return undefined;
  // Instances are numbered per function, so a bare instance number can
  // match a power meter AND a speed sensor. One match is an answer; two
  // is a coin toss.
  const matches = [SENSOR_FUNCTION.POWER, SENSOR_FUNCTION.SPEED]
    .flatMap((func) => roster(run, func))
    .filter((e) => e.id !== primary.id && e.instance === instance);
  return matches.length === 1 ? matches[0] : undefined;
}

/** The calibration fields that decide which wheel sensor is primary. */
export interface SpeedCalibration extends LegacySpeedFields {
  ground_speed_setup?: GroundSpeedSetup | null;
}

/**
 * The wheel sensor the CALIBRATION names as primary, and how to describe
 * it — the same answer the Calibration screen shows, derived the same way
 * (`effectivePrimaryId`, whose rules cover a config that leaves
 * `primary_id` unset). Null when the calibration does not settle it: GPS
 * as the speed source, or an ambiguous roster.
 */
function calibratedPrimary(
  setup: GroundSpeedSetup,
): { id: number; identity: string } | null {
  const id = effectivePrimaryId(setup, speedSource(setup));
  if (id == null) return null;
  const sensor = (setup.sensors ?? []).find((sr) => sr.id_ === id);
  return { id, identity: sensorIdentity(id, sensor?.position) };
}

/**
 * Wheel positions live on the CONFIG, and a run's own claim often has
 * none — the legacy backfill recovers ids and primary from the device's
 * sensor dump but never position, which never existed there. So a sensor
 * the run cannot place falls back to where the calibration says it is.
 * The run's own claim still wins when it has one: it is the closer record.
 */
function placed(
  entry: RosterEntry | undefined, setup: GroundSpeedSetup | null,
): RosterEntry | undefined {
  if (!entry || !setup) return entry;
  if (entry.position != null && entry.position !== SPEED_POSITION.UNKNOWN) return entry;
  const position = (setup.sensors ?? []).find((sr) => sr.id_ === entry.id)?.position;
  return position == null ? entry : { ...entry, position };
}

/**
 * The two wheel sensors behind `WhlRspd_n` and `WhlRspd_alt_n`. Speed is
 * the one type whose selection is recorded as an id rather than an
 * instance, so it resolves on runs that carry a claim and no observation;
 * `wheelSensorPair` covers the older payloads that still carry a stored
 * `ground_speed_setup` instead.
 *
 * Pass `calibration` and the roles are checked against it: `alt_spd` and a
 * lost id binding can both leave the pipeline plotting a different sensor
 * as primary than the calibration names, and a plot that quietly says
 * "Primary" for the other sensor contradicts the Calibration screen. Where
 * they disagree both roles are starred and `conflict` carries the note
 * that explains the star.
 */
export function wheelSpeedLabels(
  run: AuxSensorRun | null | undefined,
  calibration?: SpeedCalibration | null,
): WheelSpeedLabels {
  const calSetup = calibration
    ? effectiveSetup(calibration.ground_speed_setup, calibration) : null;
  const pair = wheelPair(run);
  const primary = placed(pair.primary, calSetup);
  const alt = placed(pair.alt, calSetup);
  const calibrated = calSetup ? calibratedPrimary(calSetup) : null;
  const conflict = primary && calibrated && calibrated.id !== primary.id
    ? {
      calibrated: calibrated.identity,
      used: sensorIdentity(primary.id, primary.position),
      note: `* the solve used ${sensorIdentity(primary.id, primary.position)} as the primary `
        + `wheel sensor; the calibration names ${calibrated.identity}`,
    }
    : undefined;
  return {
    primary: named(primary, PRIMARY_ROLE, !!conflict),
    alt: named(alt, AUX_ROLE, !!conflict),
    conflict,
  };
}

/** Roster-resolved wheel pair, falling back to a stored setup. */
function wheelPair(
  run: AuxSensorRun | null | undefined,
): { primary?: RosterEntry; alt?: RosterEntry } {
  const entries = roster(run, SENSOR_FUNCTION.SPEED);
  if (entries.length) {
    return resolvePair(entries, {
      id: run?.solve?.selections?.speed?.ant_device_number,
      altSelector: run?.solve?.inputs?.alt_spd,
    });
  }
  const stored = wheelSensorPair(run);
  const asEntry = (sr?: { id_: number; position?: number }): RosterEntry | undefined => (
    sr ? { id: sr.id_, position: sr.position, instance: null, claimedPrimary: false } : undefined
  );
  return { primary: asEntry(stored.primary), alt: asEntry(stored.alt) };
}

/**
 * The wind/aero probe behind the raw pressure, air-speed and yaw
 * channels. Unlike power, cadence and speed there is no alternate-source
 * selector for wind — the pipeline always reads the primary — so the sole
 * probe, or the one the claim marks primary, is the answer. Several
 * probes with no claim between them leaves it unnamed.
 */
export function windSensorLabel(
  run: AuxSensorRun | null | undefined,
): AuxSensorName | undefined {
  const entries = roster(run, SENSOR_FUNCTION.WIND);
  const entry = entries.length === 1 ? entries[0] : entries.find((e) => e.claimedPrimary);
  return named(entry, PRIMARY_ROLE);
}
